import { apiCall, isBackendConfigured } from './api-call'
import {
  QuickPassVerification,
  QuickPassVerificationType,
} from '../_types/quickpass-verification'
import {
  CreditCheckOrderResult,
  CreditCheckPaymentResult,
  ExchangePlaidResult,
  OrderCreditCheckPayload,
  StartIdentityVerificationResult,
  ContactVerificationChannel,
  StartContactVerificationResult,
  ConfirmContactVerificationResult,
} from './types'

/**
 * Opens a Didit verification session for the renter and returns the hosted
 * URL to send them to. Didit runs its flow on its own domain rather than in an
 * embedded widget, so the caller navigates rather than opening a modal.
 * Returns null when the backend isn't configured (no local fallback —
 * verification is a backend-only capability).
 *
 * @param {string} quickPassUserId
 * @returns {Promise<StartIdentityVerificationResult | null>}
 */
const startIdentityVerification = async (
  quickPassUserId: string,
): Promise<StartIdentityVerificationResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'createidentityverification',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as StartIdentityVerificationResult
  } catch (error) {
    console.warn('[verifications] startIdentityVerification failed —', error)
    return null
  }
}

/**
 * Pulls the latest Didit status from the backend and returns the refreshed
 * verification. Called the moment the renter lands back from Didit's hosted
 * flow, so the UI reflects the outcome without waiting on the webhook.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<QuickPassVerification | null>}
 */
const syncIdentityVerification = async (
  quickPassUserId: string,
): Promise<QuickPassVerification | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'syncidentityverification',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassVerification
  } catch (error) {
    console.warn('[verifications] syncIdentityVerification failed —', error)
    return null
  }
}

/**
 * Requests a Plaid Link token for the renter so the frontend can open Plaid
 * Link. Returns null when the backend isn't configured.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<string | null>} the link token
 */
const createPlaidLinkToken = async (
  quickPassUserId: string,
): Promise<string | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'createplaidlinktoken',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const parsed = JSON.parse(response.data) as { linkToken: string }
    return parsed.linkToken
  } catch (error) {
    console.warn('[verifications] createPlaidLinkToken failed —', error)
    return null
  }
}

/**
 * Hands a Plaid public token (from a successful Link flow) to the backend,
 * which exchanges it and verifies funds + income. Returns the resulting
 * statuses, or null on failure.
 *
 * @param {string} quickPassUserId
 * @param {string} publicToken
 * @returns {Promise<ExchangePlaidResult | null>}
 */
const exchangePlaidPublicToken = async (
  quickPassUserId: string,
  publicToken: string,
): Promise<ExchangePlaidResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'exchangeplaidpublictoken',
      data: { quickPassUserId, publicToken },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ExchangePlaidResult
  } catch (error) {
    console.warn('[verifications] exchangePlaidPublicToken failed —', error)
    return null
  }
}

/**
 * Shows or hides one proof on the renter's public share page. This is the
 * renter's own switch and nothing else writes it, so a provider landing a
 * result can never publish something they chose to keep private.
 *
 * @param {string} quickPassUserId
 * @param {QuickPassVerificationType} verificationType
 * @param {boolean} isPublic
 * @returns {Promise<QuickPassVerification[] | null>} the updated verifications
 */
const setVerificationVisibility = async (
  quickPassUserId: string,
  verificationType: QuickPassVerificationType,
  isPublic: boolean,
): Promise<QuickPassVerification[] | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'setverificationvisibility',
      data: { quickPassUserId, verificationType, isPublic },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassVerification[]
  } catch (error) {
    console.warn('[verifications] setVerificationVisibility failed', error)
    return null
  }
}

/**
 * Authorises the credit check fee on the renter's card and returns the client
 * secret for the embedded Payment Element. The money is only reserved: the
 * backend captures it after Certn accepts the case and releases it otherwise.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<CreditCheckPaymentResult | null>}
 */
const createCreditCheckPayment = async (
  quickPassUserId: string,
): Promise<CreditCheckPaymentResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'createcreditcheckpayment',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as CreditCheckPaymentResult
  } catch (error) {
    console.warn('[verifications] createCreditCheckPayment failed', error)
    return null
  }
}

/**
 * Orders a Canadian credit check for the renter. The backend records the
 * consent that authorised it and rejects the order outright when consent is
 * missing, so this must only ever be called after the renter has ticked the
 * consent box. It also captures the authorisation named by `paymentIntentId`,
 * so this must only run once the renter has confirmed their card.
 *
 * @param {OrderCreditCheckPayload} payload
 * @returns {Promise<CreditCheckOrderResult | null>}
 */
const orderCreditCheck = async (
  payload: OrderCreditCheckPayload,
): Promise<CreditCheckOrderResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'ordercreditcheck',
      data: {
        ...payload,
        userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as CreditCheckOrderResult
  } catch (error) {
    console.warn('[verifications] orderCreditCheck failed', error)
    return null
  }
}

/**
 * Re-reads the renter's credit check from Certn and reapplies it to their
 * profile. Used when a renter wants to move a check along rather than wait for
 * the webhook to land.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<CreditCheckOrderResult | null>}
 */
const syncCreditCheck = async (
  quickPassUserId: string,
): Promise<CreditCheckOrderResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'synccreditcheck',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as CreditCheckOrderResult
  } catch (error) {
    console.warn('[verifications] syncCreditCheck failed', error)
    return null
  }
}

/**
 * Asks the backend to send a one-time code to an email address or phone number.
 *
 * Returns the named outcome rather than throwing on a refusal, because a
 * cooldown or a mistyped address is something the renter needs told, not an
 * error to swallow. Null is reserved for the call genuinely not happening.
 *
 * @param {string} quickPassUserId
 * @param {ContactVerificationChannel} channel - which contact method to prove
 * @param {string} destination - the address or number to send the code to
 * @returns {Promise<StartContactVerificationResult | null>}
 */
const startContactVerification = async (
  quickPassUserId: string,
  channel: ContactVerificationChannel,
  destination: string,
): Promise<StartContactVerificationResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'startcontactverification',
      data: { quickPassUserId, channel, destination },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as StartContactVerificationResult
  } catch (error) {
    console.warn('[verifications] startContactVerification failed', error)
    return null
  }
}

/**
 * Submits the code the renter typed and, when it holds up, returns the recorded
 * verification so the caller can show the badge without refetching the profile.
 *
 * @param {string} quickPassUserId
 * @param {ContactVerificationChannel} channel - which contact method is being proven
 * @param {string} code - the one-time code the renter entered
 * @returns {Promise<ConfirmContactVerificationResult | null>}
 */
const confirmContactVerification = async (
  quickPassUserId: string,
  channel: ContactVerificationChannel,
  code: string,
): Promise<ConfirmContactVerificationResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'confirmcontactverification',
      data: { quickPassUserId, channel, code },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ConfirmContactVerificationResult
  } catch (error) {
    console.warn('[verifications] confirmContactVerification failed', error)
    return null
  }
}

export {
  startIdentityVerification,
  syncIdentityVerification,
  createPlaidLinkToken,
  exchangePlaidPublicToken,
  createCreditCheckPayment,
  orderCreditCheck,
  syncCreditCheck,
  setVerificationVisibility,
  startContactVerification,
  confirmContactVerification,
}
